import { getProgramColorScheme } from './ui';
import { parseError } from './parser';
import i18n from '../i18n';
import memberApi from './api/memberApi';

async function initApp({ dispatch, urlToken }) {
  console.log('initializing app...');
  try {
    let me = null;
    const state = {
      appConfig: await memberApi.getAppConfig(),
      program: await memberApi.getProgram(),
      lang: getlocalStorageLng('tc')
    };
    state.colorScheme = getProgramColorScheme(state.program.theme);

    // First priority is to login with urlToken
    if (urlToken) {
      try {
        // Use urlToken to login
        await memberApi.walletLogin({
          hashToken: decodeURIComponent(urlToken),
          clientId: state.appConfig?.['auth_client_id']
        });
        localStorage.setItem('wallet_token', urlToken);
      } catch (e) {
        console.error(e.message);
      }
    }

    // if not logged in, check if auth token can recover in localStorage
    const auth = {
      token_type: localStorage.getItem('token_type'),
      access_token: localStorage.getItem('access_token'),
      refresh_token: localStorage.getItem('refresh_token')
    };
    if (auth.token_type && auth.access_token && auth.refresh_token) {
      memberApi.setToken(auth);
    }

    // Get me state from server, logout auth token if failed
    try { me = await memberApi.getMe(); }
    catch (e) { memberApi.logout(); }

    reloadAccountProps({ dispatch, me, state });
    if (state.appConfig?.recaptcha) loadRecaptcha(state.appConfig?.recaptcha);
  } catch (error) {
    throw Error(error);
  }
}

async function reloadAccountProps({ dispatch, me = null, state = {} }) {
  console.log('loading member content...');
  try {
    if (!me) {
      try {
        me = await memberApi.getMe();
      } catch (e) {
        console.log('not logged in or invalid token');
      }
    }

    const loadAccProps = me?.status === 'activated';
    if (me && !loadAccProps) console.log('member has not complete registration yet');
    if (me && loadAccProps) console.log('member has complete registration, load member properties');

    state.init = true;
    state.me = me;
    state.missions = loadAccProps ? await memberApi.getMissions({ types: ['stamp'] }) : null;
    state.items = loadAccProps ? await memberApi.getItems({ page: 1, pageSize: 5, status: 'available' }) : null;
    state.redeemables = loadAccProps ? await memberApi.getRedeemable({ page: 1, pageSize: 10 }) : null;
    state.tierMission = loadAccProps ? await memberApi.getTierMission() : null;
    state.tierColor = loadAccProps ? state.tierMission?.current_tier?.color : null;
    state.lang = state.me?.preferences?.language ?? getlocalStorageLng('tc');
    await changeLocale({
      lang: state.lang,
      timezone: state.me?.preferences?.timezone,
      dispatch: dispatch
    });
    dispatch({ type: 'INIT', payload: state });
  } catch (error) {
    dispatch({
      type: 'SET_ERROR_DIALOG',
      payload: { open: true, ...parseError(error) }
    });
  }
}

function logoutAccount({ dispatch }) {
  memberApi.logout(() => {
    dispatch({ type: 'INIT', payload: {
      me: null,
      missions: null,
      items: null,
      redeemables: null,
      tierMission: null,
      tierColor: null
    } });
  });
}

async function changeLocale({ lang, timezone, dispatch, updateRemote = false }) {
  if (lang === 'en') {
    localStorage.setItem('i18nextLng', 'en-US');
    i18n.changeLanguage('en-US');
    memberApi.setLanguage('en-US');
    dispatch({ type: 'SWITCH_LANGUAGE', payload: 'en' });
  }
  if (lang === 'tc') {
    localStorage.setItem('i18nextLng', 'zh-HK');
    i18n.changeLanguage('zh-HK');
    memberApi.setLanguage('zh-HK');
    dispatch({ type: 'SWITCH_LANGUAGE', payload: 'tc' });
  }

  if (lang === 'sc') {
    localStorage.setItem('i18nextLng', 'zh-CN');
    i18n.changeLanguage('zh-CN');
    memberApi.setLanguage('zh-CN');
    dispatch({ type: 'SWITCH_LANGUAGE', payload: 'sc' });
  }
  // update user preference from server
  if (updateRemote) {
    try {
      await memberApi.patchPreference({ lang: lang, timezone: timezone });
    } catch (error) {}
  }
}

async function loadRecaptcha(recaptchaSiteKey) {
  const script = document.createElement('script');
  script.src = `https://www.recaptcha.net/recaptcha/api.js?render=${recaptchaSiteKey}`;
  script.async = true;
  script.defer = true;
  document.body.appendChild(script);
}

async function getGrecaptchaToken(recaptchaSiteKey, action = 'submit') {
  return await window.grecaptcha?.execute(recaptchaSiteKey, { action }) ?? null;
}

function getlocalStorageLng(defaultValue = 'tc') {
  const i18nextLng = localStorage.getItem('i18nextLng') || null;
  switch (i18nextLng) {
    case 'tc':
    case 'zh-HK':
    case 'zh-TW':
    case 'zh-MO':
      return 'tc';
    case 'sc':
    case 'zh-CN':
    case 'zh-SG':
    case 'zh-MY':
      return 'sc';
    case 'en':
    case 'en-US':
      return 'en';
    default:
      return defaultValue;
  }
}

export {
  initApp,
  reloadAccountProps,
  logoutAccount,
  changeLocale,
  getGrecaptchaToken
};